import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { NvAlertDialog, NvButton, NvEmptyState, NvErrorState } from "@/components/navigator";
import { NvCheckSmallIcon, NvTrashIcon } from "@/components/navigator/nv-icons-mypage";
import { useRequest } from "@/hooks/use-request";
import { getApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { setMark } from "@/services/navigator/finder-service";
import { listCompare } from "@/services/navigator/my-page-service";

import { MyAgentCard } from "./my-agent-card";
import { MyCompareDialog } from "./my-compare-dialog";
import { CARD_GRID, CardSkeletons, FinderLink, RETRY_LATER } from "./my-card-parts";
import { MyPageHeader, useMyPage } from "./my-page-shared";

// 비교함 (Figma ver.7 847:47855 · 제안 02 C안): 카드 왼쪽 위에 선택 체크(고정) · 오른쪽 위에 삭제 휴지통, 선택하면 카드 테두리가 주황.
// 선택은 최대 3개(탭을 옮기면 초기화) · 1개 이상 고르면 아래 바에 [선택 해제] [비교하기 (n)].
// [비교하기]: 2개 이상 → 비교표 팝업(847:50660), 1개 이하 → 안내 팝업(847:50977). 4번째를 고르면 최대 3개 안내(847:51458).

const SELECT_LIMIT = 3;
const NEED_TWO = "비교할 에이전트를 2개 이상 선택해 주세요";
const OVER_LIMIT = "최대 3개까지 선택한 후 비교할 수 있어요.\n선택한 에이전트를 해제한 뒤 다시 선택해 주세요.";

// 선택 체크 22 x 22, r5: 선택 = 주황 채움 + 흰 ✓ (바깥 흰 테두리 2 · 그림자), 미선택 = 흰 칸 + 1.5 #9EA6B0, 선택 시도(3개 초과) = 2 주황 테두리
function CompareCheck({ checked, tried, onChange, label }: { checked: boolean; tried: boolean; onChange: (next: boolean) => void; label: string }) {
  return (
    // 카드 바깥 모서리에서 12 (테두리 1이 먼저 있어 안쪽 기준 11)
    <label className="absolute left-[11px] top-[11px] z-10 flex h-[22px] w-[22px] cursor-pointer items-center justify-center">
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
      <span
        aria-hidden
        className={cn(
          "flex h-[22px] w-[22px] items-center justify-center rounded-[5px] text-white peer-focus-visible:ring-2 peer-focus-visible:ring-nv-brand peer-focus-visible:ring-offset-2",
          checked
            ? "bg-nv-brand shadow-[0_0_0_2px_#FFFFFF,0_1px_3px_rgba(0,0,0,0.18)]"
            : tried
              ? "border-2 border-nv-brand bg-nv-surface"
              : "border-[1.5px] border-[#9EA6B0] bg-nv-surface",
        )}
      >
        {checked && <NvCheckSmallIcon />}
      </span>
    </label>
  );
}

export function MyCompareSection() {
  const { refresh, barSlot } = useMyPage();
  const { data, error, loading, reload } = useRequest((signal) => listCompare(signal), []);
  const [alert, setAlert] = useState<string | null>(null);
  // 최대 3개 안내를 띄운 카드: 안내를 닫을 때까지 체크칸에 주황 테두리 (Figma "미선택 (선택 시도)")
  const [triedId, setTriedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [tableOpen, setTableOpen] = useState(false);
  // 탭을 옮기면 선택은 비워져요 (섹션이 새로 마운트됨). 목록이 바뀌면 사라진 항목은 선택에서 뺀다.
  useEffect(() => {
    if (data) setSelected((cur) => cur.filter((id) => data.items.some((a) => a.id === id)));
  }, [data]);

  const closeAlert = () => {
    setAlert(null);
    setTriedId(null);
  };
  const remove = async (id: string) => {
    try {
      await setMark("compare", id, false);
      reload();
      refresh();
    } catch (err) {
      setAlert(getApiError(err)?.message ?? RETRY_LATER);
    }
  };
  const toggleSelect = (id: string, next: boolean) => {
    if (next && selected.length >= SELECT_LIMIT) {
      setTriedId(id);
      setAlert(OVER_LIMIT);
      return;
    }
    setSelected((cur) => (next ? [...cur, id] : cur.filter((x) => x !== id)));
  };
  const compare = () => {
    if (selected.length < 2) {
      setAlert(NEED_TWO);
      return;
    }
    setTableOpen(true);
  };
  const columns = data ? data.items.map((card, index) => ({ card, index })).filter(({ card }) => selected.includes(card.id)) : [];
  const limit = data?.limit ?? 20;

  const bar = selected.length > 0 && (
    <div className="border-y border-nv-border bg-nv-surface">
      <div className="mx-auto flex h-[74px] w-full max-w-[1440px] items-center justify-between gap-4 px-5 xl:px-[120px]">
        <div className="flex min-w-0 items-center gap-3">
          <span className="whitespace-nowrap text-[16px] font-bold leading-[19px] text-nv-text-primary">{selected.length}개 선택됨</span>
          <span className="text-[14px] leading-[17px] text-[#6A6F77]">최대 {SELECT_LIMIT}개까지 함께 비교할 수 있어요</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setSelected([])}
            className="h-[42px] rounded-nv-control border border-nv-border-strong bg-nv-surface px-5 text-[15px] font-medium leading-[18px] text-nv-text-primary hover:bg-nv-bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
          >
            선택 해제
          </button>
          <button
            type="button"
            onClick={compare}
            className="h-10 rounded-nv-control bg-nv-brand px-5 text-[15px] font-bold leading-[18px] text-nv-text-inverse hover:bg-nv-brand-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand focus-visible:ring-offset-2"
          >
            비교하기 ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MyPageHeader title="비교함" description={`${data?.items.length ?? 0}개 · 최대 ${limit}개까지 담을 수 있어요 · 최대 ${SELECT_LIMIT}개까지 선택해 비교할 수 있어요`} />
      {!!error && <NvErrorState error={error} onRetry={reload} />}
      {loading && !data && <CardSkeletons />}
      {data && data.items.length === 0 && <NvEmptyState action={<FinderLink />}>비교함이 비어 있어요. 에이전트 상세에서 [비교함 담기]로 최대 {data.limit}개까지 담을 수 있어요.</NvEmptyState>}
      <div className={CARD_GRID}>
        {data?.items.map((card) => {
          const on = selected.includes(card.id);
          return (
            <MyAgentCard
              key={card.id}
              card={card}
              selected={on}
              corner={<CompareCheck checked={on} tried={triedId === card.id} onChange={(next) => toggleSelect(card.id, next)} label={`${card.name} 비교 선택`} />}
              action={
                <button
                  type="button"
                  onClick={() => remove(card.id)}
                  aria-label={`${card.name} 비교함에서 삭제`}
                  className="relative z-10 flex h-8 w-8 items-center justify-center rounded-nv-chip bg-nv-bg-subtle text-[#8E939A] hover:text-nv-text-primary"
                >
                  <NvTrashIcon />
                </button>
              }
            />
          );
        })}
      </div>
      {/* 하단 바가 붙을 때 카드와의 간격: 본문 아래 여백 80 + 20 = 100 (Figma) */}
      {selected.length > 0 && <div aria-hidden />}
      {bar && barSlot && createPortal(bar, barSlot)}

      <MyCompareDialog open={tableOpen} onOpenChange={setTableOpen} columns={columns} rows={data?.rows ?? []} />
      <NvAlertDialog open={alert !== null} onOpenChange={(o) => !o && closeAlert()} message={alert ?? ""} actions={<NvButton onClick={closeAlert}>확인</NvButton>} />
    </>
  );
}
